// components/tutorial/tutorial-view-switcher.tsx
// Toggle between All tutorials and Series view
"use client";

import { memo, useCallback } from "react";

import { cn } from "@vllnt/ui";
import { useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";

export type TutorialView = "all" | "series";

export type TutorialViewSwitcherProps = {
  currentView: TutorialView;
};

function TutorialViewSwitcherImpl({
  currentView,
}: TutorialViewSwitcherProps): React.ReactNode {
  const t = useTranslations("tutorials");
  const { push } = useRouter();
  // oxlint-disable-next-line react-doctor/nextjs-no-use-search-params-without-suspense -- page-level Suspense wraps this consumer; refactor deferred
  const searchParameters = useSearchParams();

  const handleViewChange = useCallback(
    (view: TutorialView) => {
      const parameters = new URLSearchParams(searchParameters.toString());
      if (view === "all") {
        parameters.delete("view");
      } else {
        parameters.set("view", view);
      }
      const queryString = parameters.toString();
      push(queryString ? `?${queryString}` : "?", { scroll: false });
    },
    [push, searchParameters],
  );

  return (
    <div className="flex items-center gap-1 p-1 bg-muted rounded-lg">
      <button
        className={cn(
          "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
          currentView === "all"
            ? "bg-background text-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground",
        )}
        onClick={() => {
          handleViewChange("all");
        }}
        type="button"
      >
        {t("view_all")}
      </button>
      <button
        className={cn(
          "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
          currentView === "series"
            ? "bg-background text-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground",
        )}
        onClick={() => {
          handleViewChange("series");
        }}
        type="button"
      >
        {t("view_series")}
      </button>
    </div>
  );
}

export const TutorialViewSwitcher = memo(TutorialViewSwitcherImpl);
TutorialViewSwitcher.displayName = "TutorialViewSwitcher";
